import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core'
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  List,
  LayoutGrid,
  Filter,
  Plus,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Mail,
  Webhook,
  FileSpreadsheet,
  Sparkles,
  User,
  Calendar,
  MoreHorizontal,
  Archive,
  Trash2,
  GripVertical,
  Pencil,
  Check,
  X,
  Star,
  MoveLeft,
  MoveRight,
} from 'lucide-react'
import type {
  GemsViewProps,
  Gem,
  Status,
  SourceType,
  Stream,
  GemFilterParams,
} from './types'
import { Pagination } from '../ui/Pagination'

type ViewMode = 'list' | 'board'

interface FilterState {
  sourceType: SourceType | null
  statusId: string | null
  streamId: string | null
  tag: string | null
  dateFrom: string | null
  dateTo: string | null
}

// Helper to parse URL params into filter state
function getFiltersFromUrl(): FilterState {
  if (typeof window === 'undefined') {
    return {
      sourceType: null,
      statusId: null,
      streamId: null,
      tag: null,
      dateFrom: null,
      dateTo: null,
    }
  }

  const params = new URLSearchParams(window.location.search)
  return {
    sourceType: (params.get('source') as SourceType) || null,
    statusId: params.get('status') || null,
    streamId: params.get('stream') || null,
    tag: params.get('tag') || null,
    dateFrom: params.get('from') || null,
    dateTo: params.get('to') || null,
  }
}

// Convert FilterState to GemFilterParams for callbacks
function toFilterParams(filters: FilterState): GemFilterParams {
  return {
    streamId: filters.streamId,
    sourceType: filters.sourceType,
    statusId: filters.statusId,
    tag: filters.tag,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  }
}

export function GemsView({
  statuses,
  availableTags,
  streams = [],
  gems,
  pagination,
  streamContext,
  onViewItem,
  onViewGem,
  onChangeStatus,
  onUpdateTags,
  onArchive,
  onDelete,
  onCreate,
  onAddStatus,
  onUpdateStatus,
  onDeleteStatus,
  onReorderStatus,
  onBack,
  onPageChange,
  onFilterChange,
}: GemsViewProps) {
  // Only show board view option when in stream context
  const canShowBoardView = !!streamContext

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (!canShowBoardView) return 'list'
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('gems-view-mode')
      if (saved === 'list' || saved === 'board') {
        return saved
      }
    }
    return 'list'
  })
  const [filters, setFilters] = useState<FilterState>(getFiltersFromUrl)

  // Show filters if any URL params are set
  const [showFilters, setShowFilters] = useState(() => {
    const initialFilters = getFiltersFromUrl()
    return !!(
      initialFilters.sourceType ||
      initialFilters.statusId ||
      initialFilters.streamId ||
      initialFilters.tag ||
      initialFilters.dateFrom ||
      initialFilters.dateTo
    )
  })

  // Apply filter changes to server
  const applyFilters = useCallback((newFilters: FilterState) => {
    if (onFilterChange) {
      onFilterChange(toFilterParams(newFilters))
    }
  }, [onFilterChange])

  // Update filters and trigger server request
  const updateFilters = useCallback((updater: (prev: FilterState) => FilterState) => {
    setFilters(prev => {
      const newFilters = updater(prev)
      applyFilters(newFilters)
      return newFilters
    })
  }, [applyFilters])

  // Persist view mode to localStorage
  useEffect(() => {
    localStorage.setItem('gems-view-mode', viewMode)
  }, [viewMode])
  const [newStatusName, setNewStatusName] = useState('')
  const [showAddStatus, setShowAddStatus] = useState(false)

  // Sort statuses by order
  const sortedStatuses = useMemo(
    () => [...statuses].sort((a, b) => a.order - b.order),
    [statuses]
  )

  // Gems are already filtered by server, just use them directly
  const filteredGems = gems

  // Group gems by status for board view
  const gemsByStatus = useMemo(() => {
    const grouped: Record<string, Gem[]> = {}
    sortedStatuses.forEach((status) => {
      grouped[status.id] = filteredGems.filter((gem) => gem.statusId === status.id)
    })
    return grouped
  }, [filteredGems, sortedStatuses])

  const activeFilterCount = [
    filters.sourceType,
    filters.statusId,
    filters.streamId,
    filters.tag,
    filters.dateFrom || filters.dateTo,
  ].filter(Boolean).length

  const clearFilters = () => {
    updateFilters(() => ({
      sourceType: null,
      statusId: null,
      streamId: null,
      tag: null,
      dateFrom: null,
      dateTo: null,
    }))
  }

  const getStatusById = (id: string) => statuses.find((s) => s.id === id)

  const handleAddStatus = () => {
    if (newStatusName.trim() && onAddStatus) {
      onAddStatus(newStatusName.trim())
      setNewStatusName('')
      setShowAddStatus(false)
    }
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <header className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
        <div className={`${viewMode === 'board' ? 'max-w-7xl' : 'max-w-5xl'} mx-auto px-6 py-5`}>
          {/* Back button for stream context */}
          {streamContext && onBack && (
            <button
              onClick={onBack}
              className="inline-flex items-center gap-1 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors mb-4"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Back to Stream</span>
            </button>
          )}

          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <h1 className="text-xl font-semibold text-stone-900 dark:text-stone-100">
                  {streamContext ? `${streamContext.name} Gems` : 'Gems'}
                </h1>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-xs font-medium">
                  <Sparkles className="w-3 h-3" />
                  <span className="hidden sm:inline">Flagged Items</span>
                </span>
              </div>
              <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">
                {filteredGems.length} of {pagination?.totalCount ?? gems.length} gem{(pagination?.totalCount ?? gems.length) !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {/* View Mode Toggle - Only show in stream context */}
              {canShowBoardView && (
                <div className="inline-flex rounded-lg bg-stone-100 dark:bg-stone-800 p-1">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'list'
                        ? 'bg-white dark:bg-stone-700 text-stone-900 dark:text-stone-100 shadow-sm'
                        : 'text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300'
                    }`}
                  >
                    <List className="w-4 h-4" />
                    List
                  </button>
                  <button
                    onClick={() => setViewMode('board')}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'board'
                        ? 'bg-white dark:bg-stone-700 text-stone-900 dark:text-stone-100 shadow-sm'
                        : 'text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300'
                    }`}
                  >
                    <LayoutGrid className="w-4 h-4" />
                    Board
                  </button>
                </div>
              )}

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                  showFilters || activeFilterCount > 0
                    ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300'
                    : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Filters</span>
                {activeFilterCount > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-teal-600 text-white text-xs font-medium">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Flag Item */}
              <button
                onClick={onCreate}
                className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg bg-teal-600 hover:bg-teal-700 text-white transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Flag Item</span>
                <span className="sm:hidden">Flag</span>
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-stone-200 dark:border-stone-800">
              <div className="flex flex-wrap items-end gap-4">
                {/* Stream Filter - Only show when not in stream context */}
                {!streamContext && streams.length > 0 && (
                  <div className="flex-1 min-w-[140px] max-w-[180px]">
                    <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                      Stream
                    </label>
                    <div className="relative">
                      <select
                        value={filters.streamId || ''}
                        onChange={(e) =>
                          updateFilters((prev) => ({
                            ...prev,
                            streamId: e.target.value || null,
                          }))
                        }
                        className="w-full appearance-none px-3 py-2 pr-8 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                      >
                        <option value="">All streams</option>
                        {streams.map((stream) => (
                          <option key={stream.id} value={stream.id}>
                            {stream.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 dark:text-stone-500 pointer-events-none" />
                    </div>
                  </div>
                )}

                {/* Source Filter */}
                <div className="flex-1 min-w-[140px] max-w-[160px]">
                  <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                    Source
                  </label>
                  <div className="relative">
                    <select
                      value={filters.sourceType || ''}
                      onChange={(e) =>
                        updateFilters((prev) => ({
                          ...prev,
                          sourceType: (e.target.value as SourceType) || null,
                        }))
                      }
                      className="w-full appearance-none px-3 py-2 pr-8 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                    >
                      <option value="">All sources</option>
                      <option value="email">Email</option>
                      <option value="webhook">Webhook</option>
                      <option value="csv">CSV</option>
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 dark:text-stone-500 pointer-events-none" />
                  </div>
                </div>

                {/* Status Filter - Only show in stream context since statuses are stream-specific */}
                {streamContext && (
                  <div className="flex-1 min-w-[140px] max-w-[180px]">
                    <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                      Status
                    </label>
                    <div className="relative">
                      <select
                        value={filters.statusId || ''}
                        onChange={(e) =>
                          updateFilters((prev) => ({
                            ...prev,
                            statusId: e.target.value || null,
                          }))
                        }
                        className="w-full appearance-none px-3 py-2 pr-8 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                      >
                        <option value="">All statuses</option>
                        {sortedStatuses.map((status) => (
                          <option key={status.id} value={status.id}>
                            {status.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 dark:text-stone-500 pointer-events-none" />
                    </div>
                  </div>
                )}

                {/* Tag Filter */}
                <div className="flex-1 min-w-[140px] max-w-[180px]">
                  <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                    Tag
                  </label>
                  <div className="relative">
                    <select
                      value={filters.tag || ''}
                      onChange={(e) =>
                        updateFilters((prev) => ({
                          ...prev,
                          tag: e.target.value || null,
                        }))
                      }
                      className="w-full appearance-none px-3 py-2 pr-8 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                    >
                      <option value="">All tags</option>
                      {availableTags.map((tag) => (
                        <option key={tag} value={tag}>
                          {tag}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 dark:text-stone-500 pointer-events-none" />
                  </div>
                </div>

                {/* Date From */}
                <div className="flex-1 min-w-[130px] max-w-[150px]">
                  <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                    From
                  </label>
                  <input
                    type="date"
                    value={filters.dateFrom || ''}
                    onChange={(e) =>
                      updateFilters((prev) => ({
                        ...prev,
                        dateFrom: e.target.value || null,
                      }))
                    }
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                  />
                </div>

                {/* Date To */}
                <div className="flex-1 min-w-[130px] max-w-[150px]">
                  <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                    To
                  </label>
                  <input
                    type="date"
                    value={filters.dateTo || ''}
                    onChange={(e) =>
                      updateFilters((prev) => ({
                        ...prev,
                        dateTo: e.target.value || null,
                      }))
                    }
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                  />
                </div>

                {activeFilterCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="px-3 py-2 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className={`${viewMode === 'board' ? 'max-w-7xl' : 'max-w-5xl'} mx-auto px-6 py-6`}>
        {filteredGems.length === 0 ? (
          <EmptyState onCreate={onCreate} hasFilters={activeFilterCount > 0} />
        ) : viewMode === 'list' ? (
          <>
            <ListView
              gems={filteredGems}
              statuses={statuses}
              getStatusById={getStatusById}
              showStatus={!!streamContext}
              onViewItem={onViewItem}
              onViewGem={onViewGem}
              onChangeStatus={onChangeStatus}
              onArchive={onArchive}
              onDelete={onDelete}
            />
            {pagination && onPageChange && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                perPage={pagination.perPage}
                onPageChange={(page) => onPageChange(page, toFilterParams(filters))}
              />
            )}
          </>
        ) : (
          <BoardView
            gemsByStatus={gemsByStatus}
            statuses={sortedStatuses}
            onViewItem={onViewItem}
            onViewGem={onViewGem}
            onChangeStatus={onChangeStatus}
            onArchive={onArchive}
            onDelete={onDelete}
            showAddStatus={showAddStatus}
            setShowAddStatus={setShowAddStatus}
            newStatusName={newStatusName}
            setNewStatusName={setNewStatusName}
            onAddStatus={handleAddStatus}
            onUpdateStatus={onUpdateStatus}
            onDeleteStatus={onDeleteStatus}
            onReorderStatus={onReorderStatus}
          />
        )}
      </main>
    </div>
  )
}

// =============================================================================
// List View
// =============================================================================

interface ListViewProps {
  gems: Gem[]
  statuses: Status[]
  getStatusById: (id: string) => Status | undefined
  showStatus?: boolean
  onViewItem?: (itemId: string) => void
  onViewGem?: (gemId: string) => void
  onChangeStatus?: (gemId: string, statusId: string) => void
  onArchive?: (gemId: string) => void
  onDelete?: (gemId: string) => void
}

function ListView({
  gems,
  statuses,
  getStatusById,
  showStatus = false,
  onViewItem,
  onViewGem,
  onChangeStatus,
  onArchive,
  onDelete,
}: ListViewProps) {
  return (
    <div className="space-y-2">
      {gems.map((gem) => {
        const status = getStatusById(gem.statusId)
        return (
          <GemRow
            key={gem.id}
            gem={gem}
            status={status}
            statuses={statuses}
            showStatus={showStatus}
            onView={() => onViewGem?.(gem.id)}
            onViewItem={() => onViewItem?.(gem.itemId)}
            onChangeStatus={(statusId) => onChangeStatus?.(gem.id, statusId)}
            onArchive={() => onArchive?.(gem.id)}
            onDelete={() => onDelete?.(gem.id)}
          />
        )
      })}
    </div>
  )
}

// =============================================================================
// Board View
// =============================================================================

interface BoardViewProps {
  gemsByStatus: Record<string, Gem[]>
  statuses: Status[]
  onViewItem?: (itemId: string) => void
  onViewGem?: (gemId: string) => void
  onChangeStatus?: (gemId: string, statusId: string) => void
  onArchive?: (gemId: string) => void
  onDelete?: (gemId: string) => void
  showAddStatus: boolean
  setShowAddStatus: (show: boolean) => void
  newStatusName: string
  setNewStatusName: (name: string) => void
  onAddStatus: () => void
  onUpdateStatus?: (statusId: string, data: { name?: string; color?: string; isDefault?: boolean }) => void
  onDeleteStatus?: (statusId: string) => void
  onReorderStatus?: (statusId: string, newPosition: number) => void
}

function BoardView({
  gemsByStatus,
  statuses,
  onViewItem,
  onViewGem,
  onChangeStatus,
  onArchive,
  onDelete,
  showAddStatus,
  setShowAddStatus,
  newStatusName,
  setNewStatusName,
  onAddStatus,
  onUpdateStatus,
  onDeleteStatus,
  onReorderStatus,
}: BoardViewProps) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const [overColumnId, setOverColumnId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  )

  const statusColorClasses: Record<string, string> = {
    blue: 'bg-teal-500',
    amber: 'bg-amber-500',
    purple: 'bg-purple-500',
    stone: 'bg-stone-400',
    green: 'bg-green-500',
    red: 'bg-red-500',
  }

  // Find gem by id across all statuses
  const findGem = (id: string): Gem | undefined => {
    for (const gems of Object.values(gemsByStatus)) {
      const gem = gems.find((g) => g.id === id)
      if (gem) return gem
    }
    return undefined
  }

  // Find which status column contains a gem
  const findStatusForGem = (gemId: string): string | undefined => {
    for (const [statusId, gems] of Object.entries(gemsByStatus)) {
      if (gems.some((g) => g.id === gemId)) {
        return statusId
      }
    }
    return undefined
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event
    if (!over) {
      setOverColumnId(null)
      return
    }

    const overId = over.id as string

    // Check if over a column directly
    const isOverColumn = statuses.some((s) => s.id === overId)
    if (isOverColumn) {
      setOverColumnId(overId)
    } else {
      // Over a card - find which column it belongs to
      const columnId = findStatusForGem(overId)
      setOverColumnId(columnId || null)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active } = event
    const activeGemId = active.id as string

    // Use the tracked overColumnId for the drop target
    if (overColumnId) {
      const currentStatus = findStatusForGem(activeGemId)
      if (currentStatus !== overColumnId) {
        onChangeStatus?.(activeGemId, overColumnId)
      }
    }

    setActiveId(null)
    setOverColumnId(null)
  }

  const handleDragCancel = () => {
    setActiveId(null)
    setOverColumnId(null)
  }

  const activeGem = activeId ? findGem(activeId) : null
  const activeStatus = activeGem ? statuses.find((s) => s.id === activeGem.statusId) : null
  const activeGemCurrentStatus = activeId ? findStatusForGem(activeId) : null

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {statuses.map((status, index) => (
          <DroppableColumn
            key={status.id}
            status={status}
            gems={gemsByStatus[status.id] || []}
            statuses={statuses}
            statusColorClasses={statusColorClasses}
            onViewGem={onViewGem}
            onViewItem={onViewItem}
            onChangeStatus={onChangeStatus}
            onArchive={onArchive}
            onDelete={onDelete}
            isDropTarget={overColumnId === status.id && activeGemCurrentStatus !== status.id}
            isDragging={!!activeId}
            onUpdateStatus={onUpdateStatus}
            onDeleteStatus={onDeleteStatus}
            onReorderStatus={onReorderStatus}
            columnIndex={index}
            totalColumns={statuses.length}
          />
        ))}

        {/* Add Status Column */}
        <div className="flex-shrink-0 w-72">
          {showAddStatus ? (
            <div className="bg-stone-100 dark:bg-stone-900 rounded-xl p-3">
              <input
                type="text"
                value={newStatusName}
                onChange={(e) => setNewStatusName(e.target.value)}
                placeholder="Status name..."
                className="w-full px-3 py-2 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500 outline-none focus:border-teal-500 dark:focus:border-teal-400"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onAddStatus()
                  if (e.key === 'Escape') setShowAddStatus(false)
                }}
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={onAddStatus}
                  className="flex-1 px-3 py-1.5 text-sm font-medium bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
                >
                  Add
                </button>
                <button
                  onClick={() => setShowAddStatus(false)}
                  className="px-3 py-1.5 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddStatus(true)}
              className="w-full h-12 flex items-center justify-center gap-2 text-sm text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-400 border-2 border-dashed border-stone-200 dark:border-stone-800 hover:border-stone-300 dark:hover:border-stone-700 rounded-xl transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Status
            </button>
          )}
        </div>
      </div>

      <DragOverlay>
        {activeGem && activeStatus ? (
          <GemCardContent
            gem={activeGem}
            status={activeStatus}
            statuses={statuses}
            isDragging
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

// =============================================================================
// Droppable Column
// =============================================================================

interface DroppableColumnProps {
  status: Status
  gems: Gem[]
  statuses: Status[]
  statusColorClasses: Record<string, string>
  onViewGem?: (gemId: string) => void
  onViewItem?: (itemId: string) => void
  onChangeStatus?: (gemId: string, statusId: string) => void
  onArchive?: (gemId: string) => void
  onDelete?: (gemId: string) => void
  isDropTarget?: boolean
  isDragging?: boolean
  onUpdateStatus?: (statusId: string, data: { name?: string; color?: string; isDefault?: boolean }) => void
  onDeleteStatus?: (statusId: string) => void
  onReorderStatus?: (statusId: string, newPosition: number) => void
  columnIndex: number
  totalColumns: number
}

const COLOR_OPTIONS = [
  { value: 'blue', label: 'Blue', class: 'bg-teal-500' },
  { value: 'amber', label: 'Amber', class: 'bg-amber-500' },
  { value: 'purple', label: 'Purple', class: 'bg-purple-500' },
  { value: 'stone', label: 'Gray', class: 'bg-stone-400' },
  { value: 'green', label: 'Green', class: 'bg-green-500' },
  { value: 'red', label: 'Red', class: 'bg-red-500' },
]

function DroppableColumn({
  status,
  gems,
  statuses,
  statusColorClasses,
  onViewGem,
  onViewItem,
  onChangeStatus,
  onArchive,
  onDelete,
  isDropTarget,
  isDragging,
  onUpdateStatus,
  onDeleteStatus,
  onReorderStatus,
  columnIndex,
  totalColumns,
}: DroppableColumnProps) {
  const { setNodeRef } = useDroppable({
    id: status.id,
  })

  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState(status.name)
  const [editColor, setEditColor] = useState(status.color)
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenu])

  const handleSaveEdit = () => {
    if (editName.trim() && onUpdateStatus) {
      onUpdateStatus(status.id, { name: editName.trim(), color: editColor })
    }
    setIsEditing(false)
  }

  const handleCancelEdit = () => {
    setEditName(status.name)
    setEditColor(status.color)
    setIsEditing(false)
  }

  const handleSetDefault = () => {
    if (onUpdateStatus) {
      onUpdateStatus(status.id, { isDefault: true })
    }
    setShowMenu(false)
  }

  const handleDelete = () => {
    // Cannot delete the default status
    if (status.isDefault) {
      alert('Cannot delete the default status. Set another status as default first.')
      setShowMenu(false)
      return
    }

    const gemCount = gems.length
    if (gemCount > 0) {
      const defaultStatus = statuses.find(s => s.isDefault && s.id !== status.id)
      const message = `This status has ${gemCount} gem${gemCount !== 1 ? 's' : ''}. ${
        defaultStatus ? `They will be moved to "${defaultStatus.name}".` : ''
      } Are you sure you want to delete this status?`
      if (!confirm(message)) return
    }
    onDeleteStatus?.(status.id)
    setShowMenu(false)
  }

  const handleMoveLeft = () => {
    if (columnIndex > 0) {
      onReorderStatus?.(status.id, columnIndex - 1)
    }
    setShowMenu(false)
  }

  const handleMoveRight = () => {
    if (columnIndex < totalColumns - 1) {
      onReorderStatus?.(status.id, columnIndex + 1)
    }
    setShowMenu(false)
  }

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-72 rounded-xl transition-all duration-200 ${
        isDropTarget
          ? 'bg-teal-100 dark:bg-teal-900/30 ring-2 ring-teal-500 ring-offset-2 ring-offset-stone-50 dark:ring-offset-stone-950'
          : 'bg-stone-100 dark:bg-stone-900'
      }`}
    >
      {/* Column Header */}
      <div className={`p-3 border-b transition-colors duration-200 ${
        isDropTarget
          ? 'border-teal-200 dark:border-teal-800'
          : 'border-stone-200 dark:border-stone-800'
      }`}>
        {isEditing ? (
          <div className="space-y-2">
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full px-2 py-1 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveEdit()
                if (e.key === 'Escape') handleCancelEdit()
              }}
            />
            <div className="flex items-center gap-1">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c.value}
                  onClick={() => setEditColor(c.value as Status['color'])}
                  className={`w-5 h-5 rounded-full ${c.class} ${
                    editColor === c.value ? 'ring-2 ring-offset-1 ring-teal-500' : ''
                  }`}
                  title={c.label}
                />
              ))}
            </div>
            <div className="flex gap-1">
              <button
                onClick={handleSaveEdit}
                className="flex-1 px-2 py-1 text-xs font-medium bg-teal-600 hover:bg-teal-700 text-white rounded"
              >
                <Check className="w-3 h-3 mx-auto" />
              </button>
              <button
                onClick={handleCancelEdit}
                className="px-2 py-1 text-xs text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-300"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 group">
            <span
              className={`w-2.5 h-2.5 rounded-full ${statusColorClasses[status.color] || 'bg-stone-400'}`}
            />
            <span className={`font-medium text-sm transition-colors duration-200 ${
              isDropTarget
                ? 'text-teal-700 dark:text-teal-300'
                : 'text-stone-900 dark:text-stone-100'
            }`}>
              {status.name}
            </span>
            {status.isDefault && (
              <Star className="w-3 h-3 text-amber-500 fill-amber-500" title="Default status" />
            )}
            <span className="text-xs text-stone-400 dark:text-stone-500 ml-auto">
              {gems.length}
            </span>

            {/* Status Menu */}
            <div ref={menuRef} className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1 text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg shadow-lg z-20 py-1">
                  <button
                    onClick={() => { setIsEditing(true); setShowMenu(false) }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-700"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit
                  </button>
                  {!status.isDefault && (
                    <button
                      onClick={handleSetDefault}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-700"
                    >
                      <Star className="w-4 h-4" />
                      Set as Default
                    </button>
                  )}
                  {columnIndex > 0 && (
                    <button
                      onClick={handleMoveLeft}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-700"
                    >
                      <MoveLeft className="w-4 h-4" />
                      Move Left
                    </button>
                  )}
                  {columnIndex < totalColumns - 1 && (
                    <button
                      onClick={handleMoveRight}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-700"
                    >
                      <MoveRight className="w-4 h-4" />
                      Move Right
                    </button>
                  )}
                  {totalColumns > 1 && !status.isDefault && (
                    <button
                      onClick={handleDelete}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-stone-50 dark:hover:bg-stone-700"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Cards */}
      <SortableContext
        items={gems.map((g) => g.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className={`p-2 space-y-2 min-h-[200px] transition-colors duration-200 ${
          isDropTarget ? 'bg-teal-50/50 dark:bg-teal-900/20' : ''
        }`}>
          {gems.map((gem) => (
            <DraggableGemCard
              key={gem.id}
              gem={gem}
              status={status}
              statuses={statuses}
              onView={() => onViewGem?.(gem.id)}
              onViewItem={() => onViewItem?.(gem.itemId)}
              onChangeStatus={(statusId) => onChangeStatus?.(gem.id, statusId)}
              onArchive={() => onArchive?.(gem.id)}
              onDelete={() => onDelete?.(gem.id)}
            />
          ))}
          {isDropTarget && gems.length === 0 && (
            <div className="flex items-center justify-center h-20 border-2 border-dashed border-teal-300 dark:border-teal-600 rounded-lg text-teal-500 dark:text-teal-400 text-sm">
              Drop here
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  )
}

// =============================================================================
// Gem Row (List View) - Matches ItemRow layout
// =============================================================================

interface GemRowProps {
  gem: Gem
  status?: Status
  statuses: Status[]
  showStatus?: boolean
  onView?: () => void
  onViewItem?: () => void
  onChangeStatus?: (statusId: string) => void
  onArchive?: () => void
  onDelete?: () => void
}

function GemRow({
  gem,
  status,
  statuses,
  showStatus = false,
  onView,
  onViewItem,
  onChangeStatus,
  onArchive,
  onDelete,
}: GemRowProps) {
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenu])

  const statusColorClasses: Record<string, string> = {
    blue: 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300',
    amber: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
    purple: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
    stone: 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400',
    green: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
    red: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const SourceIcon = {
    email: Mail,
    webhook: Webhook,
    csv: FileSpreadsheet,
  }[gem.sourceType]

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowMenu(!showMenu)
  }

  const handleAction = (action?: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowMenu(false)
    action?.()
  }

  return (
    <div
      onClick={onViewItem}
      className="w-full text-left group rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-4 hover:border-teal-300 dark:hover:border-teal-700 hover:shadow-sm transition-all cursor-pointer"
    >
      <div className="flex items-start gap-4">
        {/* Icon - Amber colored for gems */}
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center group-hover:bg-amber-200 dark:group-hover:bg-amber-900/60 transition-colors">
          <SourceIcon className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-stone-900 dark:text-stone-100 truncate group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
              {gem.subject}
            </h3>
            {gem.flaggedBy === 'ai' && (
              <Sparkles className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
            )}
          </div>

          <p className="text-sm text-stone-500 dark:text-stone-400 line-clamp-2 mb-2">
            {gem.reason}
          </p>

          {/* Matching criteria badges */}
          {gem.matchingCriteria && gem.matchingCriteria.length > 0 && (
            <div className="flex items-center gap-1.5 mb-2 flex-wrap">
              {gem.matchingCriteria.map((criteria, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                >
                  <Sparkles className="w-3 h-3" />
                  {criteria}
                </span>
              ))}
            </div>
          )}

          {/* Desktop: inline metadata */}
          <div className="hidden sm:flex items-center gap-3 text-xs text-stone-400 dark:text-stone-500">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 font-medium">
              {gem.streamName}
            </span>
            <span className="text-stone-300 dark:text-stone-700">•</span>
            <span className="capitalize">{gem.sourceType}</span>
            <span className="text-stone-300 dark:text-stone-700">•</span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(gem.flaggedAt)} at {formatTime(gem.flaggedAt)}
            </span>
            {/* Status selector in metadata - only when showStatus is true */}
            {showStatus && statuses.length > 0 && (
              <>
                <span className="text-stone-300 dark:text-stone-700">•</span>
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="relative"
                >
                  <select
                    value={gem.statusId}
                    onChange={(e) => onChangeStatus?.(e.target.value)}
                    className={`appearance-none px-2 py-0.5 pr-6 text-xs font-medium rounded-full cursor-pointer outline-none ${status ? statusColorClasses[status.color] : statusColorClasses.stone}`}
                  >
                    {statuses.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none opacity-60" />
                </div>
              </>
            )}
          </div>

          {/* Mobile: stacked metadata */}
          <div className="sm:hidden flex flex-wrap items-center gap-2 text-xs text-stone-400 dark:text-stone-500">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 font-medium">
              {gem.streamName}
            </span>
            <span className="capitalize">{gem.sourceType}</span>
            <span className="text-stone-300 dark:text-stone-700">•</span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(gem.flaggedAt)}
            </span>
            {/* Status selector on mobile - only when showStatus is true */}
            {showStatus && statuses.length > 0 && (
              <div
                onClick={(e) => e.stopPropagation()}
                className="relative"
              >
                <select
                  value={gem.statusId}
                  onChange={(e) => onChangeStatus?.(e.target.value)}
                  className={`appearance-none px-2 py-0.5 pr-6 text-xs font-medium rounded-full cursor-pointer outline-none ${status ? statusColorClasses[status.color] : statusColorClasses.stone}`}
                >
                  {statuses.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none opacity-60" />
              </div>
            )}
          </div>
        </div>

        {/* Action Menu */}
        <div ref={menuRef} className="relative flex-shrink-0">
          <button
            onClick={handleMenuClick}
            className="p-1.5 rounded-lg text-stone-400 hover:text-stone-600 dark:text-stone-500 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>

          {showMenu && (
            <div className="absolute right-0 top-full mt-1 w-44 py-1 bg-white dark:bg-stone-800 rounded-lg shadow-lg border border-stone-200 dark:border-stone-700 z-10">
              <button
                onClick={handleAction(onView)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700"
              >
                <Sparkles className="w-4 h-4" />
                View Gem
              </button>
              <button
                onClick={handleAction(onArchive)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700"
              >
                <Archive className="w-4 h-4" />
                Archive
              </button>

              <div className="border-t border-stone-200 dark:border-stone-700 my-1" />

              <button
                onClick={handleAction(onDelete)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-stone-100 dark:hover:bg-stone-700"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Draggable Gem Card (Board View)
// =============================================================================

interface DraggableGemCardProps {
  gem: Gem
  status: Status
  statuses: Status[]
  onView?: () => void
  onViewItem?: () => void
  onChangeStatus?: (statusId: string) => void
  onArchive?: () => void
  onDelete?: () => void
}

function DraggableGemCard({
  gem,
  status,
  statuses,
  onView,
  onViewItem,
  onChangeStatus,
  onArchive,
  onDelete,
}: DraggableGemCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: gem.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <GemCardContent
        gem={gem}
        status={status}
        statuses={statuses}
        onView={onView}
        onViewItem={onViewItem}
        onChangeStatus={onChangeStatus}
        onArchive={onArchive}
        onDelete={onDelete}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  )
}

// =============================================================================
// Gem Card Content (Board View)
// =============================================================================

interface GemCardContentProps {
  gem: Gem
  status: Status
  statuses: Status[]
  onView?: () => void
  onViewItem?: () => void
  onChangeStatus?: (statusId: string) => void
  onArchive?: () => void
  onDelete?: () => void
  dragHandleProps?: Record<string, unknown>
  isDragging?: boolean
}

function GemCardContent({
  gem,
  status,
  statuses,
  onView,
  onViewItem,
  onChangeStatus,
  onArchive,
  onDelete,
  dragHandleProps,
  isDragging,
}: GemCardContentProps) {
  const [showMenu, setShowMenu] = useState(false)

  const statusColorClasses: Record<string, string> = {
    blue: 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300',
    amber: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
    purple: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
    stone: 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400',
    green: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
    red: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
  }

  const SourceIcon = {
    email: Mail,
    webhook: Webhook,
    csv: FileSpreadsheet,
  }[gem.sourceType]

  return (
    <div
      onClick={onViewItem}
      className={`group bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg p-3 hover:border-teal-300 dark:hover:border-teal-600 hover:shadow-sm transition-all cursor-pointer ${isDragging ? 'shadow-lg ring-2 ring-teal-500' : ''}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-medium text-sm text-stone-900 dark:text-stone-100 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors line-clamp-2">
          {gem.subject}
        </h4>
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative flex-shrink-0"
        >
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 rounded opacity-0 group-hover:opacity-100 transition-all"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMenu(false)}
              />
              <div className="absolute right-0 top-full mt-1 w-36 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg shadow-lg z-20 py-1">
                <button
                  onClick={() => {
                    onArchive?.()
                    setShowMenu(false)
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-700"
                >
                  <Archive className="w-4 h-4" />
                  Archive
                </button>
                <button
                  onClick={() => {
                    onDelete?.()
                    setShowMenu(false)
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-stone-50 dark:hover:bg-stone-700"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Reason */}
      <p className="text-xs text-stone-500 dark:text-stone-400 line-clamp-2 mb-2">
        {gem.reason}
      </p>

      {/* Matching criteria */}
      {gem.matchingCriteria && gem.matchingCriteria.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {gem.matchingCriteria.slice(0, 2).map((criteria, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
            >
              <Sparkles className="w-2.5 h-2.5" />
              {criteria}
            </span>
          ))}
          {gem.matchingCriteria.length > 2 && (
            <span className="text-[10px] text-stone-400 dark:text-stone-500">
              +{gem.matchingCriteria.length - 2}
            </span>
          )}
        </div>
      )}

      {/* Stream */}
      <p className="text-xs text-stone-400 dark:text-stone-500 mb-3">
        {gem.streamName}
      </p>

      {/* Tags */}
      {gem.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {gem.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="px-1.5 py-0.5 text-xs bg-stone-100 dark:bg-stone-700 text-stone-600 dark:text-stone-400 rounded"
            >
              {tag}
            </span>
          ))}
          {gem.tags.length > 3 && (
            <span className="text-xs text-stone-400 dark:text-stone-500">
              +{gem.tags.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Date and Source */}
      <div className="flex items-center justify-between text-xs text-stone-400 dark:text-stone-500 mb-3">
        <div className="flex items-center gap-1.5">
          <SourceIcon className="w-3.5 h-3.5" />
          <span>{formatDate(gem.flaggedAt)}</span>
        </div>
        {gem.flaggedBy === 'ai' ? (
          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
        ) : (
          <User className="w-3.5 h-3.5" />
        )}
      </div>

      {/* Status and Drag Handle - Bottom */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex items-center gap-2 pt-2 border-t border-stone-100 dark:border-stone-700"
      >
        <div className="relative flex-1">
          <select
            value={gem.statusId}
            onChange={(e) => onChangeStatus?.(e.target.value)}
            className={`w-full appearance-none px-2.5 py-1 pr-7 text-xs font-medium rounded-full cursor-pointer outline-none ${statusColorClasses[status.color] || statusColorClasses.stone}`}
          >
            {statuses.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none opacity-60" />
        </div>
        <div
          {...dragHandleProps}
          className="flex-shrink-0 p-1 text-stone-300 dark:text-stone-600 hover:text-stone-400 dark:hover:text-stone-500 cursor-grab active:cursor-grabbing rounded hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors"
        >
          <GripVertical className="w-4 h-4" />
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Empty State
// =============================================================================

interface EmptyStateProps {
  onCreate?: () => void
  hasFilters: boolean
}

function EmptyState({ onCreate, hasFilters }: EmptyStateProps) {
  return (
    <div className="text-center py-16">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/40 mb-4">
        <Sparkles className="w-6 h-6 text-amber-600 dark:text-amber-400" />
      </div>
      <p className="text-stone-600 dark:text-stone-400 font-medium">
        {hasFilters ? 'No gems match your filters' : 'No gems yet'}
      </p>
      <p className="text-sm text-stone-500 dark:text-stone-500 mt-1 mb-4">
        {hasFilters
          ? 'Try adjusting your filters to see more results'
          : 'Gems will appear here when items are flagged by AI or manually'}
      </p>
      {!hasFilters && onCreate && (
        <button
          onClick={onCreate}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-teal-600 hover:bg-teal-700 text-white transition-colors"
        >
          <Plus className="w-4 h-4" />
          Flag an Item
        </button>
      )}
    </div>
  )
}
